import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import CosmicBackground from '../components/CosmicBackground';

const About = () => {
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const expertiseAreas = [
    { title: "Career Guidance", icon: "💼", desc: "Professional path analysis", color: "from-blue-500 to-indigo-600" },
    { title: "Relationship", icon: "💕", desc: "Love & marriage compatibility", color: "from-pink-500 to-rose-600" },
    { title: "Health", icon: "🌟", desc: "Wellness & vitality insights", color: "from-green-500 to-emerald-600" },
    { title: "Finance", icon: "💰", desc: "Wealth & prosperity guidance", color: "from-yellow-500 to-orange-600" },
    { title: "Education", icon: "📚", desc: "Academic success analysis", color: "from-purple-500 to-violet-600" },
    { title: "Legal Matters", icon: "⚖️", desc: "Court case guidance", color: "from-red-500 to-pink-600" },
    { title: "Progeny", icon: "👶", desc: "Child-related consultations", color: "from-teal-500 to-cyan-600" },
    { title: "Spiritual Growth", icon: "🧘", desc: "Karmic lessons & healing", color: "from-indigo-500 to-purple-600" }
  ];

  return (
    <CosmicBackground variant="about">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="min-h-screen relative z-10"
      >
        <div className="pt-32 pb-20">
          <div className="max-w-6xl mx-auto px-4">
            
            {/* About Avantika Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-20"
            >
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg rounded-2xl shadow-lg overflow-hidden border border-white/20 p-8">
                
                {/* Photo Section */}
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="text-center mb-8"
                >
                  <div className="inline-block p-4 bg-gradient-to-br from-purple-600/20 to-blue-600/20 rounded-2xl">
                    <img
                      src="/about.jpg"
                      alt="Avantika Tripathi"
                      className="w-48 h-48 lg:w-56 lg:h-56 object-cover rounded-full border-4 border-white/30 shadow-xl mx-auto"
                    />
                  </div>
                </motion.div>

                {/* Content Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                >
                  <h1 className="text-3xl lg:text-5xl font-bold mb-6 text-center bg-gradient-to-r from-yellow-300 to-yellow-200 bg-clip-text text-transparent leading-tight py-2">
                    ✨ About Avantika Tripathi ✨
                  </h1>
                  
                  <div className="space-y-6 text-gray-200 leading-relaxed max-w-4xl mx-auto">
                    <p className="text-lg text-center">
                      Avantika Tripathi is a leading and renowned Astrologer based in Pune, holding <strong className="text-yellow-400">15+ years of experience</strong> in Vedic Astrology. She has consulted <strong className="text-purple-300">25000+ people</strong> across the globe and won many Awards for her excellence in Vedic Astrology.
                    </p>
                    
                    <p className="text-lg text-center">
                      Her articles have been published in the leading magazines like <em className="text-pink-300">Greh Lakshmi, Bonobology</em> etc. and she has provided excellent Astrological guidance and solutions to thousands of people for Career, Finance, Marriage, Relationship, Progeny, Health, Education and Legal Matters.
                    </p>
                    
                    <p className="text-lg text-center">
                      Avantika is highly acknowledged by many renowned Astrologers, Politicians and Celebrities for her work. She maintains a credible and well-known presence on social media for her astrological insights and has been accredited for her work by various platforms.
                    </p>
                    
                    <p className="text-lg text-center">
                      She is highly committed to serve more and more people with her authentic and genuine approach towards Astrology. Avantika is known for her deep work and knowledge in the field of Astrology. She guides her clients by offering them the factual analysis of their birth chart, along with excellent guidance and remedies for the issues they are facing.
                    </p>
                  </div>
                  
                  {/* Action Buttons */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.6 }}
                    className="mt-12 flex flex-wrap justify-center gap-4"
                  >
                    <motion.button
                      onClick={() => navigate('/pricing')}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-black px-8 py-3 rounded-full font-bold shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      🌟 Book Consultation
                    </motion.button>
                    <motion.a
                      href="https://www.quora.com/profile/Avantika-Tripathi-2"
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="border-2 border-purple-400 text-purple-200 px-8 py-3 rounded-full font-bold hover:bg-purple-400/20 hover:text-white transition-all duration-300"
                    >
                      🔮 View Profile
                    </motion.a>
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>

            {/* YouTube Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-20"
            >
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white leading-tight py-1">
                  Avantika's Astrohelp YouTube Channel
                </h2>
                <p className="text-lg text-gray-200">
                  14.7K subscribers • Enlightening astrological insights and guidance
                </p>
              </div>
              
              <div className="max-w-4xl mx-auto bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg rounded-2xl shadow-lg p-6 border border-white/20">
                <div className="w-full bg-gradient-to-br from-purple-900/20 to-blue-900/20 rounded-xl backdrop-blur-sm relative overflow-hidden" style={{ aspectRatio: '16/9' }}>
                  <iframe
                    className="w-full h-full rounded-xl"
                    src="https://www.youtube.com/embed/Kkp3uLH8S0s"
                    title="Avantika's Astrohelp YouTube Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </motion.div>

            {/* Expertise Areas */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-20"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-white leading-tight py-1">
                Areas of Cosmic Expertise
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {expertiseAreas.map((area, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1, duration: 0.6 }}
                    whileHover={{ y: -5, scale: 1.02 }}
                    className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg p-6 rounded-xl text-center border border-white/20 hover:border-yellow-400/50 transition-all duration-300"
                  >
                    <div className="text-4xl mb-4">{area.icon}</div>
                    <h3 className="text-lg font-bold mb-2 text-white">{area.title}</h3>
                    <p className="text-gray-300 text-sm">{area.desc}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Contact CTA */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 backdrop-blur-lg rounded-2xl border border-purple-400/30 shadow-lg p-12">
                <h3 className="text-3xl font-bold mb-4 text-white">
                  Ready to Transform Your Life?
                </h3>
                <p className="text-lg mb-8 text-gray-200 max-w-2xl mx-auto leading-relaxed">
                  Connect with Avantika Tripathi for personalized astrological guidance and discover your true potential through cosmic wisdom.
                </p>
                <motion.button
                  onClick={() => navigate('/pricing')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-black px-10 py-4 rounded-full font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  🌟 Schedule Your Consultation
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </CosmicBackground>
  );
};

export default About;
